import { produce } from 'immer'
import React from 'react'

import {
  EditDiffButton,
  type EditDiffButtonProps,
} from '~/components/features/EditDiffButton'
import { type Question } from '~/hooks/useSurveyQuery'

import { useSubQuestions } from '../hooks/useSubQuestions'
import { useAtomContext } from '../providers/AtomsProvider'
import { type QuestionsDiff } from '../utils'

import { QuestionSelector } from './QuestionSelector'

export type EditQuestionsDiffButtonProps = {
  question: Question
}

export const EditQuestionsDiffButton = ({
  question,
}: EditQuestionsDiffButtonProps) => {
  const [questionsDiffsMap, setQuestionsDiffsMap] =
    useAtomContext('questionsDiffsMap')
  const diffs = React.useMemo<EditDiffButtonProps['diffs']>(
    () => questionsDiffsMap[question.question_id] ?? [],
    [questionsDiffsMap, question.question_id],
  )

  const subQuestions = useSubQuestions(question)
  const renderSelector = React.useMemo<EditDiffButtonProps['renderSelector']>(
    () => (field, anotherFieldValue) => (
      <QuestionSelector
        {...field}
        questions={subQuestions.filter(
          (subQuestion) => subQuestion.question_id !== anotherFieldValue,
        )}
      />
    ),
    [subQuestions],
  )

  const handleExecute = React.useCallback<EH<EditDiffButtonProps, 'onExecute'>>(
    (diffs) => {
      setQuestionsDiffsMap(
        produce((draft) => {
          const questionsDiffs = diffs as QuestionsDiff[]
          if (questionsDiffs.length === 0) {
            delete draft[question.question_id]
          } else {
            draft[question.question_id] = questionsDiffs
          }
        }),
      )
    },
    [question.question_id, setQuestionsDiffsMap],
  )

  return (
    <EditDiffButton
      diffs={diffs}
      renderSelector={renderSelector}
      onExecute={handleExecute}
    />
  )
}
